import _pino from 'pino';
import { _get } from '@epihub/core/src/fn/object';
import { isBrowser } from '@epihub/core/src/process/browserOrNode';
import { type PackageConst } from '@epihub/core/src/epikit/types';
import { _isArray } from '@epihub/core/src/fn/array';

// export const loggerContext = createContext({
//   name: 'logger',
//   allowNesting: true,
// });

export const pino = _pino;

export class Logger {
  // static async enterWith<Fn extends () => Promise<any>>(
  //   data: any,
  //   func: Fn,
  // ): Promise<AsyncReturnType<Fn>> {
  //   const AsyncContext = getAsyncContext();
  //   if (!AsyncContext) {
  //     return func();
  //   }
  //
  //   const namespace = AsyncContext.getNamespace('LogContext')
  //     ?? AsyncContext.createNamespace('LogContext');
  //
  //   return namespace.run(
  //     {
  //       ...namespace.getStore(),
  //       ...data,
  //     },
  //     func,
  //   );
  // }

  private static mainLogger: ReturnType<typeof Logger.overrideDefaultLogger> = Logger.overrideDefaultLogger();
  static restartMainLogger() {
    Logger.mainLogger = Logger.overrideDefaultLogger();
  }
  static getMainLogger() {
    return Logger.mainLogger;
  }

  static overrideDefaultLogger(
    options: Omit<_pino.LoggerOptions, 'browser'> = {},
    streams: _pino.StreamEntry[] = [],
  ) {
    // All default options that apply to both browsers and node
    const mergedOptions: _pino.LoggerOptions = {
      level: process.env.NODE_ENV === 'production' ? 'info' : 'trace',
      // Don't use Process, because of cyclic imports
      ...process.env.LOG_FORMAT === 'pretty'
        ? {
          transport: {
            target: 'pino-pretty',
            options: {
              colorize: true,
              timestampKey: 'time',
              translateTime: "SYS:yyyy-mm-dd'T'HH:MM:ss.l",
              messageFormat: '[{scope}] {msg}',
              messageKey: 'msg',
              levelLabel: 'level',
              levelKey: 'level',
              ignore: 'tag,scope,projectId,packageId',
            },
          },
        }
        : {},
      ...options,
    };

    if (isBrowser) {
      if (streams.length > 0) {
        throw new Error('Streams are not allowed in a browser environment');
      }

      const logger = _pino({
        ...mergedOptions,
        browser: {
          asObject: true,
          write: (_opts) => {
            const isError = typeof _opts === 'object'
              && 'stack' in _opts;

            const isUserError = typeof _opts === 'object'
              && _get(_opts, 'name', 'ObjectError');

            const opts = _opts as any;
            if (isError && !isUserError) {
              // eslint-disable-next-line no-console
              console.error(`${opts.time} @${opts.projectId}/${opts.packageId} ${opts.msg}`, opts, opts.stack);
            } else {
              // eslint-disable-next-line no-console
              console.log(`${opts.time} @${opts.projectId}/${opts.packageId} ${opts.msg}`, opts);
            }
          },
        },
      });
      Logger.mainLogger = logger;
      return logger;
    }

    // Node.js or Deno type environment
    if (Object.keys(options).length > 0 && streams.length > 0) {
      throw new Error('In a node environment you can either use options or streams, not both');
    }

    const next = streams.length > 0
      ? _pino(_pino.multistream([...streams, { stream: process.stdout }]))
      : _pino(mergedOptions);
    Logger.mainLogger = next;
    return next;
  }

  readonly projectId: string;
  readonly packageId: string;
  readonly appVersion: string;

  constructor(params: {
    packageConst: PackageConst;
    getAdditionalPackageContext?: () => object;
  }) {
    const [atProjectId, packageId] = params.packageConst.appName.split('/');
    this.projectId = atProjectId.replace('@', '');
    this.packageId = packageId;
    this.appVersion = params.packageConst.appVersion;
  }

  readonly v = (scope: string, msg: string, data?: object) => this.log('trace', scope, msg, data);
  readonly d = (scope: string, msg: string, data?: object) => this.log('debug', scope, msg, data);
  readonly i = (scope: string, msg: string, data?: object) => this.log('info', scope, msg, data);
  readonly w = (scope: string, msg: string, data?: object) => this.log('warn', scope, msg, data);
  readonly e = (scope: string, msg: string, data?: object) => this.log('error', scope, msg, data);
  readonly f = (scope: string, msg: string, data?: object) => this.log('fatal', scope, msg, data);

  readonly pipe = {
    // eslint-disable-next-line no-console
    log: (...rest: Array<string | number | any>) => console.log(...rest),
    // eslint-disable-next-line no-console
    error: (...rest: Array<string | number | any>) => console.error(...rest),
    // eslint-disable-next-line no-console
    warn: (...rest: Array<string | number | any>) => console.warn(...rest),
  };

  private readonly log = (level: _pino.Level, scope: string, msg: string, data?: object) => {
    const expanded = data ? expandValue(data) as object : undefined;
    const extras = {
      ..._isArray(expanded) ? { data: expanded } : expanded,
      // ...this.getGlobalLogContext?.(),
    };

    Logger.mainLogger[level]({
      ...extras,
      // Don't use Process, because of cyclic imports
      tag: process.env.BUILD_TAG,
      scope,
      msg,
      projectId: this.projectId,
      packageId: this.packageId,
    });

    // LoggerEvents.send(LoggerEventLog, {
    //   level,
    //   packageId: this.packageId,
    //   projectId: this.projectId,
    //   branch: Process.env.BRANCH_NAME,
    //   appVersion: this.appVersion,
    //   commitHash: Process.env.COMMIT_HASH,
    //   version: Process.env.FULL_VERSION,
    //   scope,
    //   msg,
    //   extras,
    // });
  };

  private readonly getGlobalLogContext = () => {
    // const AsyncContext = getAsyncContext();
    // const globalLogContext = AsyncContext?.getNamespaceStore?.('LogContext');
    // return globalLogContext ?? {};
  };
}

function expandValue(value: unknown): unknown {
  if (value instanceof Error) {
    return Object.getOwnPropertyNames(value).reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = (value as any)[key];
      return acc;
    }, {});
  }

  if (typeof value === 'object' && value !== null && !_isArray(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, val]) => {
      acc[key] = expandValue(val);
      return acc;
    }, {});
  }

  return value;
}
